// v7.84 — Painel de Gestão e Desconexão de Dispositivos.
// Lista sessões ativas registradas em `{group}/__devices__` (heartbeat 60s)
// e permite revogar remotamente: o device alvo limpa a fila de pendingWrites,
// derruba o sync e desloga — deixa de injetar dados órfãos na base.
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { MonitorSmartphone, RefreshCw, Ban, Trash2, Wifi, WifiOff } from 'lucide-react';
import { listActiveDevices, revokeDeviceSession, removeDeviceRecord, getLocalDeviceId } from '@/lib/firebaseSync';
import { getCurrentBundleHash } from '@/lib/versionGuard';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';

const ONLINE_WINDOW_MS = 2.5 * 60 * 1000;

function timeAgo(ms) {
  if (!ms) return '—';
  const diff = Date.now() - ms;
  if (diff < 60000) return `${Math.max(1, Math.round(diff / 1000))}s atrás`;
  if (diff < 3600000) return `${Math.round(diff / 60000)}min atrás`;
  if (diff < 86400000) return `${Math.round(diff / 3600000)}h atrás`;
  return `${Math.round(diff / 86400000)}d atrás`;
}

function shortUa(ua = '') {
  if (/iPhone|iPad/i.test(ua)) return 'iOS';
  if (/Android/i.test(ua)) return 'Android';
  if (/Windows/i.test(ua)) return 'Windows';
  if (/Mac/i.test(ua)) return 'macOS';
  if (/Linux/i.test(ua)) return 'Linux';
  return 'Navegador';
}

export default function DevicesPage() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [cleaning, setCleaning] = useState(false);
  const myId = getLocalDeviceId();
  // v8.01 — versão rodando NESTE aparelho: referência pra sinalizar defasados
  const myVersion = getCurrentBundleHash();

  const load = useCallback(async () => {
    try {
      // v8.09 — timeout de 8s: get() do SDK pendura com socket morto e a
      // tela ficava em loop infinito de carregamento.
      const list = await Promise.race([
        listActiveDevices(),
        new Promise((_, rej) => setTimeout(() => rej(new Error('devices_timeout')), 8000)),
      ]);
      list.sort((a, b) => (b.lastSeenAt || 0) - (a.lastSeenAt || 0));
      setDevices(list);
    } catch (err) {
      console.warn('[DEVICES] load falhou:', err?.message || err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [load]);

  const handleRevoke = async () => {
    if (!target) return;
    try {
      await revokeDeviceSession(target.deviceId);
      toast.success('Sessão revogada', {
        description: 'O dispositivo limpará a fila local, será desconectado e precisará logar de novo.',
      });
      setTarget(null);
      setTimeout(load, 2500);
    } catch (err) {
      toast.error(err?.message || 'Falha ao revogar sessão');
    }
  };

  const handleRemove = async (d) => {
    try {
      await removeDeviceRecord(d.deviceId);
      toast.success('Registro órfão removido');
      load();
    } catch (err) {
      toast.error(err?.message || 'Falha ao remover registro');
    }
  };

  // v7.91 — Remove TODOS os registros offline (exceto este dispositivo)
  const handleCleanGhosts = async () => {
    const ghosts = devices.filter(
      (d) => d.deviceId !== myId && !(d.lastSeenAt && Date.now() - d.lastSeenAt < ONLINE_WINDOW_MS)
    );
    if (ghosts.length === 0) return toast.info('Nenhum dispositivo offline para limpar');
    if (!window.confirm(`Remover ${ghosts.length} registro(s) offline/fantasma? Aparelhos reais reaparecem sozinhos no próximo uso (com o mesmo ID fixo).`)) return;
    setCleaning(true);
    let ok = 0;
    for (const g of ghosts) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await removeDeviceRecord(g.deviceId);
        ok += 1;
      } catch {}
    }
    setCleaning(false);
    toast.success(`${ok} registro(s) fantasma removido(s)`);
    load();
  };

  return (
    <div data-testid="devices-page" className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MonitorSmartphone className="text-cyan-600" size={26} /> Dispositivos Conectados
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Sessões registradas no Firebase. Revogar derruba o aparelho remotamente e limpa a fila de escritas pendentes dele.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="devices-clean-ghosts-btn"
            onClick={handleCleanGhosts}
            disabled={cleaning}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm font-semibold hover:bg-amber-100 active:translate-y-[1px] disabled:opacity-50"
          >
            <Trash2 size={15} /> {cleaning ? 'Limpando…' : 'Limpar fantasmas'}
          </button>
          <button
            data-testid="devices-refresh-btn"
            onClick={load}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border text-sm hover:bg-slate-50 active:translate-y-[1px]"
          >
            <RefreshCw size={15} /> Atualizar
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : devices.length === 0 ? (
        <div data-testid="devices-empty" className="border rounded-xl p-8 text-center text-muted-foreground">
          Nenhum dispositivo registrado ainda. Os aparelhos aparecem aqui após abrirem o app com esta versão.
        </div>
      ) : (
        <div className="grid gap-3">
          {devices.map((d) => {
            const online = d.lastSeenAt && Date.now() - d.lastSeenAt < ONLINE_WINDOW_MS;
            const isMe = d.deviceId === myId;
            // v8.01 — comparação de versão: só sinaliza defasagem em builds
            // reais (hash != 'dev') e quando o aparelho reportou versão.
            const hasVersions = myVersion !== 'dev' && d.appVersion && d.appVersion !== 'dev';
            const outdated = hasVersions && d.appVersion !== myVersion;
            return (
              <div
                key={d.deviceId}
                data-testid={`device-card-${d.deviceId}`}
                className={`border rounded-xl p-4 flex items-center gap-4 ${online ? 'bg-white' : 'bg-slate-50 opacity-80'}`}
              >
                <div className={`p-2 rounded-full ${online ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-200 text-slate-500'}`}>
                  {online ? <Wifi size={20} /> : <WifiOff size={20} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold flex items-center gap-2 flex-wrap">
                    {d.nickname || shortUa(d.ua)}
                    {isMe && <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700 font-bold">ESTE DISPOSITIVO</span>}
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${online ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
                      {online ? 'ONLINE' : 'OFFLINE'}
                    </span>
                    {d.appVersion && (
                      <span
                        data-testid={`device-version-${d.deviceId}`}
                        title={outdated ? 'Este aparelho está rodando uma versão antiga do sistema — atualiza sozinho ao ficar ocioso' : 'Versão do sistema neste aparelho'}
                        className={`text-[10px] px-2 py-0.5 rounded-full font-bold font-mono ${outdated ? 'bg-amber-100 text-amber-700 border border-amber-300' : 'bg-indigo-50 text-indigo-600'}`}
                      >
                        {outdated ? '⚠ ' : 'v '}{String(d.appVersion).slice(0, 8)}{outdated ? ' — DESATUALIZADO' : ''}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 truncate">
                    {shortUa(d.ua)} • tela {d.page || '—'} • visto {timeAgo(d.lastSeenAt)} • fila: {d.queueSize ?? 0} write(s)
                  </div>
                  <div className="text-[10px] text-slate-400 font-mono truncate">{d.deviceId}</div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    data-testid={`device-revoke-${d.deviceId}`}
                    onClick={() => setTarget(d)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700 active:translate-y-[1px]"
                  >
                    <Ban size={14} /> Desconectar
                  </button>
                  {!online && !isMe && (
                    <button
                      data-testid={`device-remove-${d.deviceId}`}
                      onClick={() => handleRemove(d)}
                      title="Remover registro órfão"
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs hover:bg-slate-100 active:translate-y-[1px]"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent data-testid="device-revoke-dialog">
          <DialogHeader>
            <DialogTitle>Desconectar dispositivo?</DialogTitle>
            <DialogDescription>
              <strong>{target?.nickname || shortUa(target?.ua)}</strong> ({target?.deviceId})
              {target?.deviceId === myId && (
                <span className="block mt-1 text-red-600 font-semibold">⚠️ Este é o dispositivo que você está usando agora — você será deslogado.</span>
              )}
              <span className="block mt-2">
                O aparelho vai limpar a fila de escritas pendentes ({target?.queueSize ?? 0} write(s)), desconectar do
                Firebase e voltar pra tela de login. Ele pode logar de novo normalmente depois.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <button
              data-testid="device-revoke-cancel"
              onClick={() => setTarget(null)}
              className="px-4 py-2 rounded-lg border text-sm hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              data-testid="device-revoke-confirm"
              onClick={handleRevoke}
              className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700"
            >
              Desconectar dispositivo
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
